import { useState } from "react";
import { Form, Input } from "../../components/ui";
import { useAddClass, useTeacherPages } from "../../query/classQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import swal from "../../utils/swal";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import ClassColorPicker from "./ClassColorPicker";
import { DEFAULT_CLASSROOM_COLOR } from "../../config/classroomColors";
import "../teachers/addTeacher.css";
import "./classroom.css";

function teacherName(teacher) {
  return `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || "Teacher";
}

function TeacherPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const keyword = useDebouncedValue(query.trim(), 400);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useTeacherPages(keyword);
  const teachers = (data?.pages || []).flatMap((page) => page.teachers || []);
  const selected = teachers.find((teacher) => teacher._id === value) || (picked?._id === value ? picked : null);

  const onScroll = (event) => {
    const el = event.currentTarget;
    const nearEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (nearEnd && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="tf-teacher-picker">
      <label className="tf-teacher-search">
        <Search size={16} />
        <input
          value={query}
          placeholder="Search by name or teacher ID"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {selected ? (
        <p className="tf-teacher-picked">
          Selected: {teacherName(selected)}
          {selected.teacherId ? ` (${selected.teacherId})` : ""}
        </p>
      ) : (
        <p className="tf-hint">Choose the teacher who leads this class.</p>
      )}
      <div className="tf-teacher-list" onScroll={onScroll}>
        {isLoading ? <p className="tf-teacher-empty">Loading teachers…</p> : null}
        {!isLoading && teachers.length === 0 ? (
          <p className="tf-teacher-empty">No teachers match that search.</p>
        ) : null}
        {teachers.map((teacher) => {
          const active = teacher._id === value;
          return (
            <button
              key={teacher._id}
              type="button"
              className={`tf-teacher-row${active ? " is-selected" : ""}`}
              onClick={() => {
                setPicked(teacher);
                onChange?.(teacher._id);
              }}
            >
              <strong>{teacherName(teacher)}</strong>
              <span>{teacher.teacherId || teacher.email || "Teacher"}</span>
            </button>
          );
        })}
        {isFetchingNextPage ? <p className="tf-teacher-empty">Loading more…</p> : null}
      </div>
    </div>
  );
}

function AddClass() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { mutate: addClass, isPending: isMutating } = useAddClass();

  const onFinish = (values) => {
    addClass(
      { data: values },
      {
        onSuccess: () => {
          swal("Success", "Class added successfully", "success");
          navigate("/classes");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add class", "error");
        },
      }
    );
  };

  return (
    <div className="tf-page">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{ color: DEFAULT_CLASSROOM_COLOR }}
      >
        <section className="tf-card">
          <h2 className="tf-card-head">Class Details</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">
                Class name <span>*</span>
              </p>
              <Form.Item
                name="classroomName"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter the class name" }]}
              >
                <Input placeholder="Nursery A" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Grade <span>*</span>
              </p>
              <Form.Item
                name="classroomGrade"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter the grade" }]}
              >
                <Input placeholder="Nursery" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Batch <span>*</span>
              </p>
              <Form.Item
                name="classroomBatch"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter the batch" }]}
              >
                <Input type="number" min={1} placeholder="2026" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">
                Description <span>*</span>
              </p>
              <Form.Item
                name="description"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter a description" }]}
              >
                <Input placeholder="Morning nursery class" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">
                Section color <span>*</span>
              </p>
              <Form.Item
                name="color"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select a section color" }]}
              >
                <ClassColorPicker />
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Teacher</h2>
          <div className="tf-card-body">
            <div className="tf-field is-full">
              <p className="tf-label">Class teacher</p>
              <Form.Item name="teacher" className="tf-item mb-0">
                <TeacherPicker />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/classes")}>
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating}>
            {isMutating ? "Saving..." : "Add Class"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddClass;
